<!-- CreateWalletModal.vue -->
<template>
  <div class="modal fade" id="createWalletModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-wallet-plus text-primary me-2"></i>
            Create New E-Wallet Account
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="createWallet">
          <div class="modal-body">
            <!-- Wallet Type Selection -->
            <div class="row mb-4">
              <div class="col-12">
                <label class="form-label fw-semibold">Select Wallet Type</label>
                <div class="row g-3">
                  <div class="col-md-3" v-for="type in walletTypes" :key="type.value">
                    <div class="card border cursor-pointer wallet-type-card"
                         :class="{'border-primary bg-light': walletForm.type === type.value}"
                         @click="selectWalletType(type.value)">
                      <div class="card-body text-center p-3">
                        <i :class="type.icon + ' font-size-24 mb-2'"
                           :style="{color: walletForm.type === type.value ? '#0d6efd' : '#6c757d'}"></i>
                        <h6 class="mb-1">{{ type.label }}</h6>
                        <small class="text-muted">{{ type.description }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Account Holder Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Account Holder Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Full Name / Business Name *</label>
                <input type="text" class="form-control" v-model="walletForm.holderName"
                       placeholder="Enter full name or business name" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">ID Number / Registration Number *</label>
                <input type="text" class="form-control" v-model="walletForm.idNumber"
                       placeholder="Enter ID or registration number" required>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Phone Number *</label>
                <div class="input-group">
                  <span class="input-group-text">+254</span>
                  <input type="tel" class="form-control" v-model="walletForm.phoneNumber"
                         placeholder="712345678" required pattern="[0-9]{9}">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Email Address *</label>
                <input type="email" class="form-control" v-model="walletForm.email"
                       placeholder="user@example.com" required>
              </div>
            </div>

            <!-- Address Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Address Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label">County *</label>
                <select class="form-select" v-model="walletForm.county" @change="loadSubCounties()" required>
                  <option value="">Select County</option>
                  <option v-for="county in counties" :key="county.id" :value="county">
                    {{ county.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Sub County</label>
                <select class="form-select" v-model="walletForm.subCounty" @change="loadWards()">
                  <option value="">Select Sub County</option>
                  <option v-for="subCounty in subCounties" :key="subCounty.id" :value="subCounty">
                    {{ subCounty.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Ward</label>
                <select class="form-select" v-model="walletForm.ward">
                  <option value="">Select Ward</option>
                  <option v-for="ward in wards" :key="ward.id" :value="ward">
                    {{ ward.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-12">
                <label class="form-label">Physical Address</label>
                <textarea class="form-control" v-model="walletForm.address"
                          placeholder="Enter physical address" rows="2"></textarea>
              </div>
            </div>

            <!-- Initial Deposit -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Initial Deposit (Optional)</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Deposit Amount</label>
                <div class="input-group">
                  <span class="input-group-text">KES</span>
                  <input type="number" class="form-control" v-model="walletForm.initialDeposit"
                         placeholder="0.00" min="0" step="0.01">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Deposit Method</label>
                <select class="form-select" v-model="walletForm.depositMethod">
                  <option value="">No Initial Deposit</option>
                  <option value="MPESA">M-Pesa</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CASH">Cash Deposit</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>
            </div>

            <!-- Wallet Features -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Wallet Features & Limits</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Wallet Tier</label>
                <select class="form-select" v-model="walletForm.tier" @change="updateLimits()">
                  <option v-for="tier in getAvailableTiers()" :key="tier.value" :value="tier.value">
                    {{ tier.label }} - {{ formatPrice(tier.dailyLimit) }}/day
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Currency</label>
                <select class="form-select" v-model="walletForm.currency">
                  <option value="KES">Kenyan Shilling (KES)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>
            </div>

            <!-- Service Activation -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Activate Services</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.services.mobileApp"
                         id="mobileApp">
                  <label class="form-check-label" for="mobileApp">
                    <i class="mdi mdi-cellphone text-primary me-2"></i>
                    Mobile App Access
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.services.webPortal"
                         id="webPortal">
                  <label class="form-check-label" for="webPortal">
                    <i class="mdi mdi-web text-info me-2"></i>
                    Web Portal Access
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.services.smsNotifications"
                         id="smsNotifications">
                  <label class="form-check-label" for="smsNotifications">
                    <i class="mdi mdi-message-text text-success me-2"></i>
                    SMS Notifications
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.services.physicalCard"
                         id="physicalCard">
                  <label class="form-check-label" for="physicalCard">
                    <i class="mdi mdi-credit-card text-warning me-2"></i>
                    Physical Debit Card
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.services.virtualCard"
                         id="virtualCard">
                  <label class="form-check-label" for="virtualCard">
                    <i class="mdi mdi-credit-card-chip text-secondary me-2"></i>
                    Virtual Card
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.services.apiAccess"
                         id="apiAccess">
                  <label class="form-check-label" for="apiAccess">
                    <i class="mdi mdi-api text-danger me-2"></i>
                    API Integration
                  </label>
                </div>
              </div>
            </div>

            <!-- Limits Preview -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-header">
                    <h6 class="mb-0">Transaction Limits Preview</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3">
                        <small class="text-muted">Single Transaction</small>
                        <div class="fw-semibold">{{ formatPrice(currentLimits.single) }}</div>
                      </div>
                      <div class="col-md-3">
                        <small class="text-muted">Daily Limit</small>
                        <div class="fw-semibold">{{ formatPrice(currentLimits.daily) }}</div>
                      </div>
                      <div class="col-md-3">
                        <small class="text-muted">Monthly Limit</small>
                        <div class="fw-semibold">{{ formatPrice(currentLimits.monthly) }}</div>
                      </div>
                      <div class="col-md-3">
                        <small class="text-muted">Annual Limit</small>
                        <div class="fw-semibold">{{ formatPrice(currentLimits.annual) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="row">
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.agreeToTerms"
                         id="agreeToTerms" required>
                  <label class="form-check-label" for="agreeToTerms">
                    I agree to the <a href="javascript: void(0);" class="text-primary">Terms and Conditions</a> and
                    <a href="javascript: void(0);" class="text-primary">Privacy Policy</a> *
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="walletForm.kycConsent"
                         id="kycConsent" required>
                  <label class="form-check-label" for="kycConsent">
                    I consent to KYC verification and data processing *
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div class="text-muted">
                <small>* Required fields</small>
              </div>
              <div>
                <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="mdi mdi-check me-2"></i>
                  {{ loading ? 'Creating...' : 'Create Wallet' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: "CreateWalletModal",
  data() {
    return {
      loading: false,
      walletForm: {
        type: 'PERSONAL',
        holderName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        county: '',
        subCounty: '',
        ward: '',
        address: '',
        initialDeposit: 0,
        depositMethod: '',
        tier: 'TIER_1',
        currency: 'KES',
        services: {
          mobileApp: true,
          webPortal: true,
          smsNotifications: true,
          physicalCard: false,
          virtualCard: true,
          apiAccess: false
        },
        agreeToTerms: false,
        kycConsent: false
      },

      walletTypes: [
        {
          value: 'PERSONAL',
          label: 'Personal',
          description: 'Individual account',
          icon: 'mdi mdi-account'
        },
        {
          value: 'BUSINESS',
          label: 'Business',
          description: 'Business account',
          icon: 'mdi mdi-office-building'
        },
        {
          value: 'GOVERNMENT',
          label: 'Government',
          description: 'Government entity',
          icon: 'mdi mdi-bank'
        },
        {
          value: 'MERCHANT',
          label: 'Merchant',
          description: 'Merchant account',
          icon: 'mdi mdi-store'
        }
      ],

      counties: [
        { id: 1, name: 'Nairobi' },
        { id: 2, name: 'Mombasa' },
        { id: 3, name: 'Kisumu' },
        { id: 4, name: 'Nakuru' },
        { id: 5, name: 'Eldoret' }
      ],

      subCounties: [],
      wards: [],

      currentLimits: {
        single: 5000,
        daily: 10000,
        monthly: 300000,
        annual: 3600000
      }
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    selectWalletType(type) {
      this.walletForm.type = type;
      this.updateDefaultTier();
    },

    updateDefaultTier() {
      const tierMap = {
        'PERSONAL': 'TIER_1',
        'BUSINESS': 'BUSINESS_TIER_1',
        'GOVERNMENT': 'GOVERNMENT',
        'MERCHANT': 'MERCHANT_BASIC'
      };
      this.walletForm.tier = tierMap[this.walletForm.type] || 'TIER_1';
      this.updateLimits();
    },

    getAvailableTiers() {
      const tiers = {
        'PERSONAL': [
          { value: 'TIER_1', label: 'Tier 1', dailyLimit: 10000 },
          { value: 'TIER_2', label: 'Tier 2', dailyLimit: 50000 },
          { value: 'TIER_3', label: 'Tier 3', dailyLimit: 100000 }
        ],
        'BUSINESS': [
          { value: 'BUSINESS_TIER_1', label: 'Business Tier 1', dailyLimit: 500000 },
          { value: 'BUSINESS_TIER_2', label: 'Business Tier 2', dailyLimit: 1000000 }
        ],
        'GOVERNMENT': [
          { value: 'GOVERNMENT', label: 'Government', dailyLimit: 10000000 }
        ],
        'MERCHANT': [
          { value: 'MERCHANT_BASIC', label: 'Merchant Basic', dailyLimit: 1000000 },
          { value: 'MERCHANT_PREMIUM', label: 'Merchant Premium', dailyLimit: 2000000 }
        ]
      };
      return tiers[this.walletForm.type] || tiers['PERSONAL'];
    },

    updateLimits() {
      const tierLimits = {
        'TIER_1': { single: 5000, daily: 10000, monthly: 300000, annual: 3600000 },
        'TIER_2': { single: 25000, daily: 50000, monthly: 1500000, annual: 18000000 },
        'TIER_3': { single: 50000, daily: 100000, monthly: 3000000, annual: 36000000 },
        'BUSINESS_TIER_1': { single: 250000, daily: 500000, monthly: 15000000, annual: 180000000 },
        'BUSINESS_TIER_2': { single: 500000, daily: 1000000, monthly: 30000000, annual: 360000000 },
        'GOVERNMENT': { single: 5000000, daily: 10000000, monthly: 300000000, annual: 3600000000 },
        'MERCHANT_BASIC': { single: 500000, daily: 1000000, monthly: 30000000, annual: 360000000 },
        'MERCHANT_PREMIUM': { single: 1000000, daily: 2000000, monthly: 60000000, annual: 720000000 }
      };

      this.currentLimits = tierLimits[this.walletForm.tier] || tierLimits['TIER_1'];
    },

    loadSubCounties() {
      if (this.walletForm.county) {
        // Simulate loading subcounties
        this.subCounties = [
          { id: 1, name: 'Central', countyId: this.walletForm.county.id },
          { id: 2, name: 'North', countyId: this.walletForm.county.id },
          { id: 3, name: 'South', countyId: this.walletForm.county.id },
          { id: 4, name: 'East', countyId: this.walletForm.county.id },
          { id: 5, name: 'West', countyId: this.walletForm.county.id }
        ];
      }
    },

    loadWards() {
      if (this.walletForm.subCounty) {
        // Simulate loading wards
        this.wards = [
          { id: 1, name: 'Ward 1', subCountyId: this.walletForm.subCounty.id },
          { id: 2, name: 'Ward 2', subCountyId: this.walletForm.subCounty.id },
          { id: 3, name: 'Ward 3', subCountyId: this.walletForm.subCounty.id },
          { id: 4, name: 'Ward 4', subCountyId: this.walletForm.subCounty.id },
          { id: 5, name: 'Ward 5', subCountyId: this.walletForm.subCounty.id }
        ];
      }
    },

    generateWalletId() {
      const prefix = 'KE';
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
      return prefix + timestamp + random;
    },

    generateAccountNumber() {
      const prefix = 'ACC-';
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
      return prefix + timestamp + random;
    },

    async createWallet() {
      try {
        this.loading = true;

        // Simulate API call
        const walletData = {
          walletId: this.generateWalletId(),
          accountNumber: this.generateAccountNumber(),
          holderName: this.walletForm.holderName,
          phoneNumber: '+254' + this.walletForm.phoneNumber,
          email: this.walletForm.email,
          type: this.walletForm.type,
          balance: this.walletForm.initialDeposit || 0,
          availableBalance: this.walletForm.initialDeposit || 0,
          status: 'ACTIVE',
          lastActivity: new Date().toISOString().slice(0, 16).replace('T', ' '),
          lastActivityType: 'Account Created',
          dateCreated: new Date().toISOString().slice(0, 10),
          kycStatus: 'PENDING',
          tier: this.walletForm.tier,
          county: this.walletForm.county?.name || '',
          subCounty: this.walletForm.subCounty?.name || '',
          ward: this.walletForm.ward?.name || '',
          address: this.walletForm.address,
          currency: this.walletForm.currency,
          services: { ...this.walletForm.services }
        };

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        this.$emit('wallet-created', walletData);

        // Reset form
        this.resetForm();

        // Close modal
        // eslint-disable-next-line
        const modal = bootstrap.Modal.getInstance(document.getElementById('createWalletModal'));
        modal.hide();

      } catch (error) {
        console.error('Error creating wallet:', error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.walletForm = {
        type: 'PERSONAL',
        holderName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        county: '',
        subCounty: '',
        ward: '',
        address: '',
        initialDeposit: 0,
        depositMethod: '',
        tier: 'TIER_1',
        currency: 'KES',
        services: {
          mobileApp: true,
          webPortal: true,
          smsNotifications: true,
          physicalCard: false,
          virtualCard: true,
          apiAccess: false
        },
        agreeToTerms: false,
        kycConsent: false
      };
      this.subCounties = [];
      this.wards = [];
      this.updateLimits();
    }
  },
  mounted() {
    this.updateLimits();
  }
}
</script>

<style scoped>
.wallet-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.wallet-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

.card {
  border-radius: 8px;
}

.modal-dialog {
  max-width: 900px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>